import React from "react";

type SelectionStateHook<TSelection> = (params: {
  scope: string;
}) => readonly [TSelection, React.Dispatch<React.SetStateAction<TSelection>>];

type Props<TSelection extends { type: string }> = {
  scope: string;
  useSelectionState: SelectionStateHook<TSelection>;
};

export const useSidePanelSelection = <TSelection extends { type: string }>({
  scope,
  useSelectionState,
}: Props<TSelection>) => {
  const [isFolded, setFolded] = React.useState<boolean>(true);
  const [selectedSidePanel, setSelectedSidePanel] = useSelectionState({
    scope,
  });

  const isSameSelection = React.useCallback((current: TSelection, next: TSelection) => {
    const hasSameTab =
      "tab" in next && "tab" in current
        ? (current as { tab: unknown }).tab === (next as { tab: unknown }).tab
        : !("tab" in current) && !("tab" in next);
    return current.type === next.type && hasSameTab;
  }, []);

  const selectSidePanel = React.useCallback(
    (next: TSelection) => {
      const hasFocusField =
        "focusField" in next && (next as { focusField?: unknown }).focusField !== undefined;

      if (!isFolded && hasFocusField && isSameSelection(selectedSidePanel, next)) {
        setSelectedSidePanel(next);
        return;
      }

      if (isSameSelection(selectedSidePanel, next) && !isFolded) {
        setFolded(true);
        return;
      }

      setSelectedSidePanel(next);
      if (isFolded) {
        setFolded(false);
      }
    },
    [isFolded, isSameSelection, selectedSidePanel, setSelectedSidePanel],
  );

  return {
    isFolded,
    setFolded,
    selectedSidePanel,
    setSelectedSidePanel,
    selectSidePanel,
    onClickSidePanel: React.useCallback(
      (next: TSelection) => () => selectSidePanel(next),
      [selectSidePanel],
    ),
  };
};
